import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnInit, OnChanges, ChangeDetectorRef, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface FilterOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss']
})
export class AutocompleteComponent implements OnInit, OnChanges {
  @Input() label: string = 'Seleccionar';
  @Input() placeholder: string = 'Buscar...';
  @Input() required: boolean = false;  // Indica si el campo es requerido (muestra asterisco)
  @Input() requireSelection: boolean = false;  // Nuevo: requiere seleccionar opción del dropdown
  @Input() disabled: boolean = false;  // Nuevo: deshabilita el campo
  @Input() set options(value: any[]) {
    this._allOptions = this.normalizeOptions(value);
    this.updateFilteredOptions();
  }
  get options(): FilterOption[] {
    return this._allOptions;
  }

  @Input() selectedValue: string | null = null;
  @Output() selectionChanged = new EventEmitter<string | null>();

  @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;

  filterControl = new FormControl<string | FilterOption>('');
  filteredOptions$!: Observable<FilterOption[]>;
  
  private _allOptions: FilterOption[] = [];

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.filteredOptions$ = this.filterControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchText = typeof value === 'string' ? value : (value?.name || '');
        return this.filter(searchText);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      // When options change via setter, preserve the selected value if it's still available
      // Re-apply the selected value if it exists
      if (this.selectedValue) {
        const option = this._allOptions.find(o => o.id === this.selectedValue);
        if (option) {
          this.filterControl.setValue(option, { emitEvent: false });
        }
      }
    }

    if (changes['disabled']) {
      if (this.disabled) {
        this.filterControl.disable({ emitEvent: false });
      } else {
        this.filterControl.enable({ emitEvent: false });
      }
    }

    if (changes['selectedValue']) {
      const newValue = changes['selectedValue'].currentValue;
      
      if (newValue) {
        const option = this._allOptions.find(o => o.id === newValue);
        if (option) {
          this.filterControl.setValue(option, { emitEvent: false });
        } else if (!this.requireSelection) {
          this.filterControl.setValue(newValue, { emitEvent: false });
        }
      } else {
        // emitEvent: true so filteredOptions$ re-evaluates and shows all options
        this.filterControl.setValue('', { emitEvent: true });
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Normaliza diferentes formatos de entrada a FilterOption[]
   */
  private normalizeOptions(value: any[]): FilterOption[] {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    if (value.length === 0) {
      return [];
    }

    const firstElement = value[0];

    // Si ya es FilterOption[]
    if (typeof firstElement === 'object' && firstElement !== null && 'id' in firstElement && 'name' in firstElement) {
      return value as FilterOption[];
    }

    // Si es string[]
    if (typeof firstElement === 'string') {
      return value.map((item: string) => ({
        id: item,
        name: item
      })) as FilterOption[];
    }

    // Si es objeto genérico, intenta mapear propiedades comunes
    if (typeof firstElement === 'object' && firstElement !== null) {
      return value.map((item: any) => {
        const id = item.id || item.code || item.Id || item.Code || String(item);
        const name = item.name || item.Name || item.label || item.Label || String(item) || id;
        return { id, name } as FilterOption;
      });
    }

    return [];
  }

  private filter(value: string): FilterOption[] {
    const filterValue = value.toLowerCase();
    return this._allOptions.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  private updateFilteredOptions(): void {
    // Trigger the observable to update
    this.filterControl.updateValueAndValidity({ emitEvent: true });
  }

  onOptionSelected(option: FilterOption): void {
    this.selectedValue = option.id;
    this.selectionChanged.emit(option.id);
    this.cdr.markForCheck();
  }

  onClear(): void {
    this.filterControl.setValue('', { emitEvent: true });
    this.selectionChanged.emit(null);
    this.cdr.markForCheck();
  }

  onFreeTextBlur(): void {
    if (this.requireSelection) return;
    const value = this.filterControl.value;
    const text = typeof value === 'string' ? value.trim() : value?.id;
    this.selectionChanged.emit(text || null);
  }

  trackByOptionId(index: number, option: FilterOption): string {
    return option.id;
  }

  displayFn(value: string | FilterOption | null): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return (value as FilterOption).name || '';
  }
}
